// src/pdf/expedienteSections.js


import {
  PREGUNTAS_NOM24_GENERALES,
  PREGUNTAS_NOM24_GERONTOLOGIA,
  PREGUNTAS_NOM24_SALUD_MATERNA,
  PREGUNTAS_NOM24_PEDIATRIA,
} from "../services/cuestionariocatalog";

// ===============================
// HELPERS NOM-024 (mismas reglas que la pestaña Consultas)
// - Vacío o -1: no se imprime
// - 0 ("No") sí se imprime
// ===============================
const tieneRespuestaNOM = (v) =>
  v !== undefined &&
  v !== null &&
  String(v).trim() !== "" &&
  String(v).trim() !== "-1";

// En mediciones (cintura, glucosa) el 0 significa "no se midió"
const tieneMedicion = (v) => tieneRespuestaNOM(v) && Number(v) !== 0;

const SI_NO = { 0: "No", 1: "Sí" };
const PRIMERA_SUBSECUENTE = { 0: "Primera vez", 1: "Subsecuente" };
const INTERVENCIONES_SMYA = {
  1: "Intervención psicosocial",
  2: "Tratamiento farmacológico",
  3: "Ambos",
};

const PREGUNTAS_NOM = [
  ...PREGUNTAS_NOM24_GENERALES,
  ...PREGUNTAS_NOM24_GERONTOLOGIA,
  ...PREGUNTAS_NOM24_SALUD_MATERNA,
  ...PREGUNTAS_NOM24_PEDIATRIA,
];

// "1" -> "Sí"; "1&3" -> "Opción 1, Opción 3"
const textoNOM = (valor, etiquetas) => {
  if (!etiquetas) return String(valor);
  return String(valor)
    .split("&")
    .map((v) => etiquetas[v.trim()] ?? v.trim())
    .join(", ");
};

// Imprime "Etiqueta: valor" solo si tiene respuesta
const escribirNOM = (engine, label, valor, etiquetas) => {
  if (!tieneRespuestaNOM(valor)) return;
  engine.writeLabelValue(label, textoNOM(valor, etiquetas));
};

// "RINOFARINGITIS AGUDA - J00X"; si no hay nombre, solo la clave
const textoCIE = (clave, nombresCIE = {}) => {
  const nombre = nombresCIE?.[String(clave).trim().toUpperCase()];
  return nombre ? `${nombre} - ${clave}` : String(clave);
};

// "95 (en ayunas · tira capilar)"
const textoGlucosa = (r) => {
  if (!tieneMedicion(r?.glucemia)) return null;
  const detalles = [];
  if (String(r?.tipoMedicion) === "1") detalles.push("en ayunas");
  if (String(r?.tipoMedicion) === "0") detalles.push("sin ayuno");
  if (String(r?.resultadoObtenidoaTravesde) === "1") detalles.push("laboratorio");
  if (String(r?.resultadoObtenidoaTravesde) === "2") detalles.push("tira capilar");
  return detalles.length ? `${r.glucemia} (${detalles.join(" · ")})` : String(r.glucemia);
};

// --- Bloques NOM que se usan en historia y en notas de evolución ---

const imprimirNOMDatosConsulta = (engine, r, catalogos = {}) => {
  escribirNOM(engine, "Servicio de atención", r?.servicioAtencion, catalogos.servicios);
  escribirNOM(engine, "Primera vez en UNEME", r?.primeraVezUneme, SI_NO);
  escribirNOM(engine, "Tipo de interrogatorio", r?.tipoInterrogatorio);
};

const imprimirNOMTb = (engine, r) => {
  escribirNOM(engine, "Sintomático respiratorio sospechoso de TB", r?.sintomaticoRespiratorioTb, SI_NO);
};

const imprimirNOMMetabolico = (engine, r) => {
  if (tieneMedicion(r?.circunferenciaCintura))
    engine.writeLabelValue("Circunferencia de cintura (cm)", r.circunferenciaCintura);
  const glucosa = textoGlucosa(r);
  if (glucosa) engine.writeLabelValue("Glucosa (mg/dl)", glucosa);
};

const imprimirNOMDiagnosticos = (engine, r, catalogos = {}) => {
  escribirNOM(engine, "Relación temporal del diagnóstico principal", r?.relacionTemporal, PRIMERA_SUBSECUENTE);

  [1, 2, 3].forEach((num) => {
    const clave = r?.[`codigoCIEDiagnostico${num}`];
    if (!tieneRespuestaNOM(clave)) return;

    if (num !== 1)
      escribirNOM(engine, `¿Primera vez del diagnóstico ${num}?`, r?.[`primeraVezDiagnostico${num}`], SI_NO);

    engine.writeLabelValue(`Diagnóstico CIE ${num}`, textoCIE(clave, catalogos.nombresCIE));
    escribirNOM(engine, `Confirmación diagnóstica ${num}`, r?.[`confirmacionDiagnostica${num}`], SI_NO);
  });
};

const imprimirNOMSaludMental = (engine, r) => {
  escribirNOM(engine, "Intervenciones de salud mental y adicciones", r?.intervencionesSMyA, INTERVENCIONES_SMYA);
};

// Rubro "Cuestionario específico": solo si alguna pregunta tiene respuesta
const imprimirNOMCuestionario = (engine, r) => {
  const respondidas = PREGUNTAS_NOM.filter((q) => tieneRespuestaNOM(r?.[q.key]));
  if (respondidas.length === 0) return;

  engine.writeSubtitle("Cuestionario específico");
  respondidas.forEach((q) => {
    engine.writeLabelValue(q.label, textoNOM(r[q.key], q.labels));
  });
};

// Quita medicamentos vacíos para que la numeración empiece en 1
const medicamentosConDatos = (lista) =>
  Array.isArray(lista)
    ? lista.filter(
        (m) =>
          String(m?.nombre || "").trim() !== "" ||
          String(m?.indicaciones || "").trim() !== ""
      )
    : [];

// ===============================
// HOJA 1 — IDENTIFICACIÓN GENERAL
// ===============================

export function imprimirIdentificacion(engine, { paciente, historiaClinica, medico }) {
  const nombreCompleto = [
    paciente?.nombre,
    paciente?.apellido_paterno,
    paciente?.apellido_materno
  ].filter(Boolean).join(" ");

  const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("es-MX", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
    : "";

  const idCorto = paciente?.id
    ? String(paciente.id).slice(-5).toUpperCase()
    : "";

  engine.writeCenteredTitle("EXPEDIENTE CLÍNICO");

  engine.writeTitle("FICHA DE IDENTIFICACIÓN");

  // --- Identificación básica ---
  engine.writeLabelValue("Nombre completo", nombreCompleto);
 
  engine.writeLabelValue("CURP", paciente?.curp);
  engine.writeLabelValue("Teléfono", paciente?.telefono);
  engine.writeLabelValue("Correo", paciente?.correo);

  // --- Demografía ---
  engine.writeLabelValue("Fecha de nacimiento", formatDate(paciente?.fecha_nacimiento));
  engine.writeLabelValue("Edad", paciente?.edad ? `${paciente.edad} años` : "");
  engine.writeLabelValue("Sexo", paciente?.sexo);
  if (paciente?.nacionalidad) {
  engine.writeLabelValue("Nacionalidad", paciente.nacionalidad);
}
  engine.writeLabelValue("Estado civil", paciente?.estado_civil);

  // --- Nacimiento ---
engine.writeLabelValue("País de nacimiento", paciente?.paisNacPaciente);
engine.writeLabelValue("Entidad de nacimiento", paciente?.entidadNacimiento);

// --- Residencia ---
engine.writeLabelValue("País de residencia", paciente?.paisResidencia);
engine.writeLabelValue("Entidad de residencia", paciente?.entidadResidencia);
engine.writeLabelValue("Municipio de residencia", paciente?.municipio);
engine.writeLabelValue("Localidad de residencia", paciente?.localidad);

  // --- Dirección ---
  engine.writeLabelValue("Dirección", [
    paciente?.direccion?.calle,
    paciente?.direccion?.colonia,
    paciente?.direccion?.ciudad,
    paciente?.direccion?.estado,
    paciente?.direccion?.codigo_postal
  ].filter(Boolean).join(", "));

  // --- Otros ---
 
  engine.writeLabelValue("Fecha registro", formatDate(paciente?.fecha_registro));
  

  engine.addVerticalSpace(5);

  engine.writeTitle("DATOS DEL PROFESIONAL DE LA SALUD");

  const nombreCompletoMedico = [
    medico?.nombre,
    medico?.apellido_paterno,
    medico?.apellido_materno,
  ].filter(Boolean).join(" ");

  engine.writeLabelValue("Nombre completo", nombreCompletoMedico);
  engine.writeLabelValue("CURP", medico?.curpPrestador);
  engine.writeLabelValue("Correo", medico?.email);
  engine.writeLabelValue("Teléfono", medico?.telefono);
  engine.writeLabelValue("Teléfono fijo", medico?.perfil?.telefono_fijo);
  engine.writeLabelValue("Nombre del establecimiento", medico?.perfil?.nombre_clinica);
   // Dirección
  engine.writeLabelValue("Dirección", [
    medico?.direccion?.calle,
    medico?.direccion?.colonia,
    medico?.direccion?.ciudad,
    medico?.direccion?.estado,
    medico?.direccion?.codigo_postal,
  ].filter(Boolean).join(", "));

  engine.addVerticalSpace(3);

  // Licenciatura
  engine.writeSubtitle("Licenciatura");
  engine.writeLabelValue("Título", medico?.perfil?.titulo_universitario);
  engine.writeLabelValue("Cédula profesional", medico?.perfil?.cedula_profesional);
  engine.writeLabelValue("Escuela", medico?.perfil?.escuela_licenciatura);

  engine.addVerticalSpace(2);

  // Especialidad (solo si hay cédula)
  if (medico?.perfil?.cedula_especialidad) {
    engine.writeSubtitle("Especialidad");
    engine.writeLabelValue("Título", medico?.especialidad);
    engine.writeLabelValue("Cédula de especialidad", medico?.perfil?.cedula_especialidad);
    engine.writeLabelValue("Escuela", medico?.perfil?.escuela_especialidad);
    engine.addVerticalSpace(2);
  }

  // Subespecialidad (solo si hay cédula)
  if (medico?.perfil?.cedula_subespecialidad) {
    engine.writeSubtitle("Subespecialidad");
    engine.writeLabelValue("Subespecialidad", medico?.perfil?.subespecialidad);
    engine.writeLabelValue("Cédula de subespecialidad", medico?.perfil?.cedula_subespecialidad);
    engine.writeLabelValue("Escuela", medico?.perfil?.escuela_subespecialidad);
    engine.addVerticalSpace(2);
  }

  // Alta especialidad (solo si hay cédula)
  if (medico?.perfil?.cedula_alta_especialidad) {
    engine.writeSubtitle("Alta especialidad");
    engine.writeLabelValue("Alta especialidad", medico?.perfil?.alta_especialidad);
    engine.writeLabelValue("Cédula de alta especialidad", medico?.perfil?.cedula_alta_especialidad);
    engine.writeLabelValue("Escuela", medico?.perfil?.escuela_alta_especialidad);
    engine.addVerticalSpace(2);
  }

  engine.addVerticalSpace(5);
  engine.addVerticalSpace(10);
  engine.startNewPage();
}

// ===============================
// BLOQUE 1 — HISTORIA CLÍNICA 6.1
// ===============================

export function imprimirHistoriaInicial(engine, historiaClinica = {}, catalogos = {}) {
  engine.writeTitle("HISTORIA CLÍNICA");
  // ID corto de historia clínica
if (historiaClinica?.id) {
  const idCortoHistoria = String(historiaClinica.id)
    .slice(-5)
    .toUpperCase();

  engine.writeLabelValue("ID Historia", idCortoHistoria);
}

// Fecha real de la historia clínica / primera consulta
const fechaHistoriaRaw =
  historiaClinica?.fechaConsulta ||
  historiaClinica?.fecha_consulta;

if (fechaHistoriaRaw) {
  const fechaHistoria = new Date(fechaHistoriaRaw).toLocaleDateString("es-MX", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

  engine.writeLabelValue("Fecha de primer consulta", fechaHistoria);
}

imprimirNOMDatosConsulta(engine, historiaClinica, catalogos);

engine.addVerticalSpace(2);
  // 6.1.1 INTERROGATORIO
  engine.writeSubtitle("INTERROGATORIO");

        // Tipo de interrogatorio (ponerlo en consulta tambien)
      //if (historiaClinica?.tipo_interrogatorio)
      //  engine.writeLabelValue(
      //    "Tipo de interrogatorio",
      //    historiaClinica.tipo_interrogatorio
      //  );

 engine.writeSubtitle("Antecedentes heredofamiliares");
    if (historiaClinica?.heredofamiliares_consolidado) {
      engine.writeParagraph(historiaClinica.heredofamiliares_consolidado);
    } else {
      if (historiaClinica?.["antecedentes.heredoFamiliares.madre"]) engine.writeLabelValue("Madre", historiaClinica["antecedentes.heredoFamiliares.madre"]);
      if (historiaClinica?.["antecedentes.heredoFamiliares.padre"]) engine.writeLabelValue("Padre", historiaClinica["antecedentes.heredoFamiliares.padre"]);
      if (historiaClinica?.["antecedentes.heredoFamiliares.hermanos"]) engine.writeLabelValue("Hermanos", historiaClinica["antecedentes.heredoFamiliares.hermanos"]);
      if (historiaClinica?.["antecedentes.heredoFamiliares.abuelos"]) engine.writeLabelValue("Abuelos", historiaClinica["antecedentes.heredoFamiliares.abuelos"]);
      if (historiaClinica?.["antecedentes.heredoFamiliares.otros"]) engine.writeLabelValue("Otros", historiaClinica["antecedentes.heredoFamiliares.otros"]);
    }

  engine.writeSubtitle("Antecedentes personales no patológicos");

    if (historiaClinica?.grupo_etnico)
      engine.writeLabelValue("Grupo étnico", historiaClinica.grupo_etnico);

    if (historiaClinica?.no_patologicos_consolidado) {
      engine.writeParagraph(historiaClinica.no_patologicos_consolidado);
    } else {
      if (historiaClinica?.["antecedentes.personalesNoPatologicos.Origen"]) engine.writeLabelValue("Origen", historiaClinica["antecedentes.personalesNoPatologicos.Origen"]);
      if (historiaClinica?.["antecedentes.personalesNoPatologicos.ocupacion"]) engine.writeLabelValue("Ocupación", historiaClinica["antecedentes.personalesNoPatologicos.ocupacion"]);
      if (historiaClinica?.["antecedentes.personalesNoPatologicos.escolaridad"]) engine.writeLabelValue("Escolaridad", historiaClinica["antecedentes.personalesNoPatologicos.escolaridad"]);
      if (historiaClinica?.["antecedentes.personalesNoPatologicos.religion"]) engine.writeLabelValue("Religión", historiaClinica["antecedentes.personalesNoPatologicos.religion"]);
      if (historiaClinica?.["antecedentes.personalesNoPatologicos.mascotas"]) engine.writeLabelValue("Mascotas", historiaClinica["antecedentes.personalesNoPatologicos.mascotas"]);
      if (historiaClinica?.["antecedentes.personalesNoPatologicos.viajes"]) engine.writeLabelValue("Viajes", historiaClinica["antecedentes.personalesNoPatologicos.viajes"]);
      if (historiaClinica?.["antecedentes.personalesNoPatologicos.alimentacion"]) engine.writeLabelValue("Alimentación", historiaClinica["antecedentes.personalesNoPatologicos.alimentacion"]);
      if (historiaClinica?.["antecedentes.personalesNoPatologicos.vivienda"]) engine.writeLabelValue("Vivienda", historiaClinica["antecedentes.personalesNoPatologicos.vivienda"]);
      if (historiaClinica?.["antecedentes.personalesNoPatologicos.otros"]) engine.writeLabelValue("Otros", historiaClinica["antecedentes.personalesNoPatologicos.otros"]);
    }
    
  engine.writeSubtitle("Antecedentes personales patológicos");

    // Campos fijos siempre
    if (historiaClinica?.["antecedentes.personalesPatologicos.ECD"])
      engine.writeLabelValue("Enfermedades crónico-degenerativas", historiaClinica["antecedentes.personalesPatologicos.ECD"]);
    if (historiaClinica?.["antecedentes.personalesPatologicos.alergias"])
      engine.writeLabelValue("Alergias", historiaClinica["antecedentes.personalesPatologicos.alergias"]);
    if (historiaClinica?.["antecedentes.personalesPatologicos.hemotipo"])
      engine.writeLabelValue("Hemotipo", historiaClinica["antecedentes.personalesPatologicos.hemotipo"]);

    if (historiaClinica?.patologicos_consolidado) {
      engine.writeParagraph(historiaClinica.patologicos_consolidado);
    } else {
      if (historiaClinica?.["antecedentes.personalesPatologicos.quirurgicos"]) engine.writeLabelValue("Quirúrgicos", historiaClinica["antecedentes.personalesPatologicos.quirurgicos"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.traumaticos"]) engine.writeLabelValue("Traumáticos", historiaClinica["antecedentes.personalesPatologicos.traumaticos"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.hospitalizaciones"]) engine.writeLabelValue("Hospitalizaciones", historiaClinica["antecedentes.personalesPatologicos.hospitalizaciones"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.infectocontagiosos"]) engine.writeLabelValue("Infectocontagiosos", historiaClinica["antecedentes.personalesPatologicos.infectocontagiosos"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.transfusionales"]) engine.writeLabelValue("Transfusionales", historiaClinica["antecedentes.personalesPatologicos.transfusionales"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.exantematicos"]) engine.writeLabelValue("Exantemáticos", historiaClinica["antecedentes.personalesPatologicos.exantematicos"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.alcohol"]) engine.writeLabelValue("Alcohol", historiaClinica["antecedentes.personalesPatologicos.alcohol"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.tabaco"]) engine.writeLabelValue("Tabaco", historiaClinica["antecedentes.personalesPatologicos.tabaco"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.toxicomanias"]) engine.writeLabelValue("Toxicomanías", historiaClinica["antecedentes.personalesPatologicos.toxicomanias"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.vacunacion"]) engine.writeLabelValue("Vacunación", historiaClinica["antecedentes.personalesPatologicos.vacunacion"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.ginecoObstetricosAndrologicos"]) engine.writeLabelValue("Gineco-obstétricos / Andrológicos", historiaClinica["antecedentes.personalesPatologicos.ginecoObstetricosAndrologicos"]);
      if (historiaClinica?.["antecedentes.personalesPatologicos.otros"]) engine.writeLabelValue("Otros", historiaClinica["antecedentes.personalesPatologicos.otros"]);
    }

  engine.writeSubtitle("Padecimiento actual");

  if (historiaClinica?.padecimientoActual)
    engine.writeParagraph(historiaClinica.padecimientoActual);

  engine.writeSubtitle("Interrogatorio por aparatos y sistemas");

   imprimirNOMTb(engine, historiaClinica);

    if (historiaClinica?.interrogatorio_consolidado) {
      engine.writeParagraph(historiaClinica.interrogatorio_consolidado);
    } else {
      if (historiaClinica?.sistema_nervioso) engine.writeLabelValue("Sistema nervioso", historiaClinica.sistema_nervioso);
      if (historiaClinica?.sistema_cardiovascular) engine.writeLabelValue("Sistema cardiovascular", historiaClinica.sistema_cardiovascular);
      if (historiaClinica?.sistema_respiratorio) engine.writeLabelValue("Sistema respiratorio", historiaClinica.sistema_respiratorio);
      if (historiaClinica?.sistema_digestivo) engine.writeLabelValue("Sistema digestivo", historiaClinica.sistema_digestivo);
      if (historiaClinica?.sistema_genitourinario) engine.writeLabelValue("Sistema genitourinario", historiaClinica.sistema_genitourinario);
      if (historiaClinica?.sistema_musculoesqueletico) engine.writeLabelValue("Sistema musculoesquelético", historiaClinica.sistema_musculoesqueletico);
      if (historiaClinica?.sistema_endocrino) engine.writeLabelValue("Sistema endocrino", historiaClinica.sistema_endocrino);
      if (historiaClinica?.sistema_hematologico) engine.writeLabelValue("Sistema hematológico", historiaClinica.sistema_hematologico);
      if (historiaClinica?.piel_anexos) engine.writeLabelValue("Piel y anexos", historiaClinica.piel_anexos);
    }

  // 6.1.2 EXPLORACIÓN FÍSICA
  engine.writeSubtitle("EXPLORACIÓN FÍSICA");

    // ===============================
    // 🔹 TEXTO DE EXPLORACIÓN
    // ===============================
    if (historiaClinica?.exploracion_otros) {
      
      engine.writeParagraph(historiaClinica.exploracion_otros);
    }

    // ===============================
    // 🔹 SIGNOS VITALES
    // ===============================
    engine.writeSubtitle("Signos vitales");

    // Peso
    if (historiaClinica?.["exploracionFisica.peso"])
      engine.writeLabelValue(
        "Peso (kg)",
        historiaClinica["exploracionFisica.peso"]
      );

    // Altura
    if (historiaClinica?.["exploracionFisica.altura"])
      engine.writeLabelValue(
        "Altura (m)",
        historiaClinica["exploracionFisica.altura"]
      );

    // Presión arterial
    if (historiaClinica?.sistolica && historiaClinica?.diastolica) {
      engine.writeLabelValue(
        "Presión arterial",
        `${historiaClinica.sistolica}/${historiaClinica.diastolica} mmHg`
      );
    }

    // FC
    if (historiaClinica?.["exploracionFisica.signosVitales.frecuenciaCardiaca"])
      engine.writeLabelValue(
        "Frecuencia cardiaca",
        historiaClinica["exploracionFisica.signosVitales.frecuenciaCardiaca"]
      );

    // FR
    if (historiaClinica?.["exploracionFisica.signosVitales.frecuenciaRespiratoria"])
      engine.writeLabelValue(
        "Frecuencia respiratoria",
        historiaClinica["exploracionFisica.signosVitales.frecuenciaRespiratoria"]
      );

    // Temperatura
    if (historiaClinica?.["exploracionFisica.signosVitales.temperatura"])
      engine.writeLabelValue(
        "Temperatura",
        historiaClinica["exploracionFisica.signosVitales.temperatura"]
      );

    // Saturación
    if (historiaClinica?.["exploracionFisica.signosVitales.saturacionOxigeno"])
      engine.writeLabelValue(
        "Saturación de oxígeno",
        historiaClinica["exploracionFisica.signosVitales.saturacionOxigeno"]
      );

    // IMC
    if (historiaClinica?.imc)
      engine.writeLabelValue("IMC", historiaClinica.imc);

    // Categoría IMC
    if (historiaClinica?.categoria_imc)
      engine.writeLabelValue("Categoría IMC", historiaClinica.categoria_imc);

    
    imprimirNOMMetabolico(engine, historiaClinica);


  // 6.1.3 ESTUDIOS
  engine.writeSubtitle("RESULTADOS PREVIOS Y ACTUALES DE ESTUDIOS");

    // 🔹 Texto libre (si existe)
    if (historiaClinica?.laboratorios_texto) {
      engine.writeParagraph(historiaClinica.laboratorios_texto);
    }

    // 🔹 Archivos adjuntos (solo nombres)
    if (Array.isArray(historiaClinica?.laboratorios) && historiaClinica.laboratorios.length > 0) {
      historiaClinica.laboratorios.forEach((lab) => {
        if (lab?.nombre) {
          engine.writeLabelValue("Estudio adjunto", lab.nombre);
        }
      });
    }

  engine.writeSubtitle("DIAGNÓSTICOS");

    imprimirNOMDiagnosticos(engine, historiaClinica, catalogos);

    if (historiaClinica?.diagnostico)
      engine.writeParagraph(historiaClinica.diagnostico);

  engine.writeSubtitle("PRONÓSTICO");

    if (historiaClinica?.pronostico)
      engine.writeParagraph(historiaClinica.pronostico);

  // 6.1.6 INDICACIÓN TERAPÉUTICA
   engine.writeSubtitle("PLAN DE TRATAMIENTO");

    imprimirNOMSaludMental(engine, historiaClinica);

    // 🔹 Medicamentos (sin renglones vacíos, numeración desde 1)
    medicamentosConDatos(historiaClinica?.medicamentos).forEach((m, idx) => {
      const nombre = (m?.nombre || "").trim();
      const indicaciones = (m?.indicaciones || "").trim();
      engine.writeParagraph(
        `${idx + 1}. ${nombre}${indicaciones ? ` — ${indicaciones}` : ""}`
      );
    });

    // 🔹 Plan nuevo
    if (historiaClinica?.tratamiento) {
      engine.writeParagraph(historiaClinica.tratamiento);
    }

    // 🔹 Plan legacy
    if (historiaClinica?.plan_tratamiento) {
      engine.writeParagraph(historiaClinica.plan_tratamiento);
    }

    // ===============================
    // INDICACIONES ADICIONALES
    // ===============================
    const indicaciones =
      historiaClinica?.indicacionesAdicionales ??
      historiaClinica?.indicaciones_adicionales;

    if (indicaciones !== null && indicaciones !== undefined && String(indicaciones).trim() !== "") {
      engine.writeSubtitle("Indicaciones adicionales");
      engine.writeParagraph(String(indicaciones).trim());
    }

      // Cuestionario específico (antes de los rubros de especialidad)
    imprimirNOMCuestionario(engine, historiaClinica);

    // ===============================
    // GINECOLOGÍA / OBSTETRICIA
    // ===============================

    const tieneGinecoHC =
      historiaClinica?.edad_menarca ||
      historiaClinica?.ciclo_menstrual ||
      historiaClinica?.vida_sexual ||
      historiaClinica?.fecha_ultimo_papanicolaou ||
      historiaClinica?.fecha_ultima_mastografia ||
      historiaClinica?.antecedentes_obstetricos ||
      historiaClinica?.antecedentes_reproductivos ||
      historiaClinica?.fecha_ultima_menstruacion ||
      historiaClinica?.factores_riesgo_embarazo ||
      historiaClinica?.exploracion_gineco_obstetrica ||
      historiaClinica?.fondo_uterino_cm ||
      historiaClinica?.movimientos_fetales ||
      historiaClinica?.frecuencia_cardiaca_fetal ||
      historiaClinica?.otros_ginecologia;

    if (tieneGinecoHC) {
      engine.writeSubtitle("Ginecología / Obstetricia");

      if (historiaClinica?.edad_menarca)
        engine.writeLabelValue("Edad menarca", historiaClinica.edad_menarca);

      if (historiaClinica?.ciclo_menstrual)
        engine.writeLabelValue("Ciclo menstrual", historiaClinica.ciclo_menstrual);

      if (historiaClinica?.vida_sexual)
        engine.writeLabelValue("Vida sexual", historiaClinica.vida_sexual);

      if (historiaClinica?.fecha_ultimo_papanicolaou)
        engine.writeLabelValue("Último papanicolaou", historiaClinica.fecha_ultimo_papanicolaou);

      if (historiaClinica?.fecha_ultima_mastografia)
        engine.writeLabelValue("Última mastografía", historiaClinica.fecha_ultima_mastografia);

      if (historiaClinica?.antecedentes_obstetricos)
        engine.writeLabelValue("Antecedentes obstétricos", historiaClinica.antecedentes_obstetricos);

      if (historiaClinica?.antecedentes_reproductivos)
        engine.writeLabelValue("Antecedentes reproductivos", historiaClinica.antecedentes_reproductivos);

      if (historiaClinica?.fecha_ultima_menstruacion)
        engine.writeLabelValue("Fecha última menstruación", historiaClinica.fecha_ultima_menstruacion);

      if (historiaClinica?.factores_riesgo_embarazo)
        engine.writeLabelValue("Factores de riesgo en embarazo", historiaClinica.factores_riesgo_embarazo);

      if (historiaClinica?.exploracion_gineco_obstetrica)
        engine.writeLabelValue("Exploración gineco-obstétrica", historiaClinica.exploracion_gineco_obstetrica);

      if (historiaClinica?.fondo_uterino_cm)
        engine.writeLabelValue("Fondo uterino (cm)", historiaClinica.fondo_uterino_cm);

      if (historiaClinica?.movimientos_fetales)  
        engine.writeLabelValue(
          "Movimientos fetales",
          historiaClinica.movimientos_fetales === true ? "Confirmado" : historiaClinica.movimientos_fetales
        );

      if (historiaClinica?.frecuencia_cardiaca_fetal)
        engine.writeLabelValue("FC fetal", historiaClinica.frecuencia_cardiaca_fetal);

      if (historiaClinica?.otros_ginecologia)
        engine.writeLabelValue("Otros (ginecología)", historiaClinica.otros_ginecologia);
    }

    // ===============================
    // PEDIATRÍA
    // ===============================

    const tienePediaHC =
      historiaClinica?.persona_responsable_paciente ||
      historiaClinica?.esquema_vacunacion ||
      historiaClinica?.peso_nacer ||
      historiaClinica?.talla_nacer ||
      historiaClinica?.perimetro_cefalico_nacer ||
      historiaClinica?.tipo_parto ||
      historiaClinica?.antecedentes_perinatales ||
      historiaClinica?.hitos_desarrollo ||
      historiaClinica?.perimetro_cefalico ||
      historiaClinica?.es_longitud ||
      historiaClinica?.longitud ||
      historiaClinica?.otros_pediatria;

    if (tienePediaHC) {
      engine.writeSubtitle("Pediatría");

      if (historiaClinica?.persona_responsable_paciente)
        engine.writeLabelValue("Responsable", historiaClinica.persona_responsable_paciente);

      if (historiaClinica?.esquema_vacunacion)
        engine.writeLabelValue("Esquema de vacunación", historiaClinica.esquema_vacunacion);

      if (historiaClinica?.peso_nacer)
        engine.writeLabelValue("Peso al nacer", historiaClinica.peso_nacer);

      if (historiaClinica?.talla_nacer)
        engine.writeLabelValue("Talla al nacer", historiaClinica.talla_nacer);

      if (historiaClinica?.perimetro_cefalico_nacer)
        engine.writeLabelValue("PC al nacer", historiaClinica.perimetro_cefalico_nacer);

      if (historiaClinica?.tipo_parto)
        engine.writeLabelValue("Tipo de parto", historiaClinica.tipo_parto);

      if (historiaClinica?.antecedentes_perinatales)
        engine.writeLabelValue("Antecedentes perinatales", historiaClinica.antecedentes_perinatales);

      if (historiaClinica?.hitos_desarrollo)
        engine.writeLabelValue("Hitos del desarrollo", historiaClinica.hitos_desarrollo);

      if (historiaClinica?.perimetro_cefalico)
        engine.writeLabelValue("Perímetro cefálico", historiaClinica.perimetro_cefalico);

      if (historiaClinica?.es_longitud)
        engine.writeLabelValue("Es longitud", historiaClinica.es_longitud ? "Confirmado" : "");

      if (historiaClinica?.longitud)
        engine.writeLabelValue("Longitud", historiaClinica.longitud);

      if (historiaClinica?.otros_pediatria)
        engine.writeLabelValue("Otros (pediatría)", historiaClinica.otros_pediatria);
    }

   // ===============================
  // GERIATRÍA
  // ===============================

  const tieneGeriaHC =
    historiaClinica?.enfermedades_cronicas ||
    historiaClinica?.apoyo_familiar ||
    historiaClinica?.caidas_ultimos_12_meses ||
    historiaClinica?.detalle_caidas_12_meses ||
    historiaClinica?.dependencia_aseo_personal ||
    historiaClinica?.esquema_vacunacion_geriatria ||
    historiaClinica?.dispositivos_movilidad ||
    historiaClinica?.otros_geriatria;

  if (tieneGeriaHC) {
    engine.writeSubtitle("Geriatría");

    if (historiaClinica?.enfermedades_cronicas)
      engine.writeLabelValue("Enfermedades crónicas", historiaClinica.enfermedades_cronicas);

    if (historiaClinica?.apoyo_familiar)
      engine.writeLabelValue("Apoyo familiar", historiaClinica.apoyo_familiar);

    if (historiaClinica?.caidas_ultimos_12_meses)
      engine.writeLabelValue(
        "Caídas últimos 12 meses",
        historiaClinica.caidas_ultimos_12_meses ? "Confirmado" : ""
      );

    if (historiaClinica?.detalle_caidas_12_meses)
      engine.writeLabelValue(
        "Detalle de caídas",
        historiaClinica.detalle_caidas_12_meses
      );

    if (historiaClinica?.dependencia_aseo_personal)
      engine.writeLabelValue(
        "Dependencia (aseo personal)",
        historiaClinica.dependencia_aseo_personal ? "Confirmado" : ""
      );

    if (historiaClinica?.esquema_vacunacion_geriatria)
      engine.writeLabelValue(
        "Vacunación (geriatría)",
        historiaClinica.esquema_vacunacion_geriatria ? "Confirmado" : ""
      );

    if (historiaClinica?.dispositivos_movilidad)
      engine.writeLabelValue(
        "Dispositivos de movilidad",
        historiaClinica.dispositivos_movilidad
      );

    if (historiaClinica?.otros_geriatria)
      engine.writeLabelValue("Otros (geriatría)", historiaClinica.otros_geriatria);
  }

  engine.startNewPage();
}

// ===============================
// BLOQUE 2 — NOTAS DE EVOLUCIÓN 6.2
// ===============================


export function imprimirNotasEvolucion(engine, consultas = [], medico, catalogos = {}) {
  consultas.forEach((consulta, index) => {
    const fechaRaw = consulta?.fechaConsulta;

let fechaFormateada = "";

if (fechaRaw) {
  const date = new Date(fechaRaw);
  fechaFormateada = date.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

engine.writeTitle(`NOTA DE EVOLUCIÓN — ${fechaFormateada}`);
// ID corto de consulta
if (consulta?.id) {
  const idCortoConsulta = String(consulta.id)
    .slice(-5)
    .toUpperCase();

  engine.writeLabelValue("ID Consulta", idCortoConsulta);
}
    imprimirNOMDatosConsulta(engine, consulta, catalogos);

    // Motivo de la consulta
    if (consulta?.motivo) {
      engine.writeSubtitle("Motivo de la consulta");
      engine.writeParagraph(consulta.motivo);
    }

    // 6.2.1 Evolución
    engine.writeSubtitle("Evolución y actualización del cuadro clínico");
      if (consulta?.evolucion)
      engine.writeParagraph(consulta.evolucion);
    imprimirNOMTb(engine, consulta);

    // ===============================
    // 🔹 EXPLORACIÓN FÍSICA
    // ===============================
    engine.writeSubtitle("Exploración física");
    engine.writeParagraph(
      consulta?.exploracion_otros
        ? consulta.exploracion_otros
        : ""
    );

    engine.writeSubtitle("Signos vitales");

      // Peso
      if (consulta?.["exploracionFisica.peso"])
        engine.writeLabelValue(
          "Peso (kg)",
          consulta["exploracionFisica.peso"]
        );

      // Altura
      if (consulta?.["exploracionFisica.altura"])
        engine.writeLabelValue(
          "Altura (m)",
          consulta["exploracionFisica.altura"]
        );

      // Presión arterial
      if (consulta?.sistolica && consulta?.diastolica)
        engine.writeLabelValue(
          "Presión arterial",
          `${consulta.sistolica}/${consulta.diastolica} mmHg`
        );
      else if (consulta?.["exploracionFisica.signosVitales.presionArterial"])
        engine.writeLabelValue(
          "Presión arterial",
          consulta["exploracionFisica.signosVitales.presionArterial"]
        );

      // Frecuencia cardiaca
      if (consulta?.["exploracionFisica.signosVitales.frecuenciaCardiaca"])
        engine.writeLabelValue(
          "Frecuencia cardiaca",
          consulta["exploracionFisica.signosVitales.frecuenciaCardiaca"]
        );

      // Frecuencia respiratoria
      if (consulta?.["exploracionFisica.signosVitales.frecuenciaRespiratoria"])
        engine.writeLabelValue(
          "Frecuencia respiratoria",
          consulta["exploracionFisica.signosVitales.frecuenciaRespiratoria"]
        );

      // Temperatura
      if (consulta?.["exploracionFisica.signosVitales.temperatura"])
        engine.writeLabelValue(
          "Temperatura",
          consulta["exploracionFisica.signosVitales.temperatura"]
        );

      // Saturación
      if (consulta?.["exploracionFisica.signosVitales.saturacionOxigeno"])
        engine.writeLabelValue(
          "Saturación de oxígeno",
          consulta["exploracionFisica.signosVitales.saturacionOxigeno"]
        );

      // IMC
      if (consulta?.imc)
        engine.writeLabelValue("IMC", consulta.imc);

      // Categoría IMC
      if (consulta?.categoria_imc)
        engine.writeLabelValue("Categoría IMC", consulta.categoria_imc);

      imprimirNOMMetabolico(engine, consulta);

    // 6.2.3 Estudios
    engine.writeSubtitle("Resultados relevantes de estudios");

    if (consulta?.laboratorios_texto)
      engine.writeParagraph(consulta.laboratorios_texto);

    if (Array.isArray(consulta?.laboratorios) && consulta.laboratorios.length > 0) {
      consulta.laboratorios.forEach((lab) => {
        if (lab?.nombre)
          engine.writeLabelValue("Estudio adjunto", lab.nombre);
      });
    }


    // 6.2.4 Diagnósticos
    engine.writeSubtitle("Diagnósticos");
    imprimirNOMDiagnosticos(engine, consulta, catalogos);

    if (consulta?.diagnostico)
      engine.writeParagraph(consulta.diagnostico);


    // 6.2.5 Pronóstico
    engine.writeSubtitle("Pronóstico");
    if (consulta?.pronostico)
      engine.writeParagraph(consulta.pronostico);


       engine.writeSubtitle("Tratamiento e indicaciones médicas");

    imprimirNOMSaludMental(engine, consulta);

    // Medicamentos (sin renglones vacíos, numeración desde 1)
    medicamentosConDatos(consulta?.medicamentos).forEach((m, idx) => {
      const nombre = (m?.nombre || "").trim();
      const indicaciones = (m?.indicaciones || "").trim();
      engine.writeParagraph(
        `${idx + 1}. ${nombre}${indicaciones ? ` — ${indicaciones}` : ""}`
      );
    });
    // Plan nuevo
    if (consulta?.tratamiento)
      engine.writeParagraph(consulta.tratamiento);

    // Plan legacy
    if (consulta?.plan_tratamiento)
      engine.writeParagraph(consulta.plan_tratamiento);

   // ===============================
    // INDICACIONES ADICIONALES
    // ===============================
    const indicaciones =
      consulta?.indicacionesAdicionales ??
      consulta?.indicaciones_adicionales;

    if (indicaciones !== null && indicaciones !== undefined && String(indicaciones).trim() !== "") {
      engine.writeSubtitle("Indicaciones adicionales");
      engine.writeParagraph(String(indicaciones).trim());
    }

    // Cuestionario específico (antes de los rubros de especialidad)
  imprimirNOMCuestionario(engine, consulta);

  // ===============================
  // GINECOLOGÍA / OBSTETRICIA
  // ===============================

  const tieneGinecoConsulta =
    consulta?.exploracion_gineco_obstetrica ||
    consulta?.fondo_uterino_cm ||
    consulta?.movimientos_fetales ||
    consulta?.frecuencia_cardiaca_fetal ||
    consulta?.otros_ginecologia;

  if (tieneGinecoConsulta) {
    engine.writeSubtitle("Ginecología / Obstetricia");

    if (consulta?.exploracion_gineco_obstetrica)
      engine.writeLabelValue(
        "Exploración gineco-obstétrica",
        consulta.exploracion_gineco_obstetrica
      );

    if (consulta?.fondo_uterino_cm)
      engine.writeLabelValue(
        "Fondo uterino (cm)",
        consulta.fondo_uterino_cm
      );

   if (consulta?.movimientos_fetales)
      engine.writeLabelValue(
        "Movimientos fetales",
        consulta.movimientos_fetales === true ? "Confirmado" : consulta.movimientos_fetales
      );

    if (consulta?.frecuencia_cardiaca_fetal)
      engine.writeLabelValue(
        "FCF (lpm)",
        consulta.frecuencia_cardiaca_fetal
      );

    if (consulta?.otros_ginecologia)
      engine.writeLabelValue(
        "Otros (Ginecología)",
        consulta.otros_ginecologia
      );
  }

    // ===============================
    // PEDIATRÍA
    // ===============================

    const tienePediaConsulta =
      consulta?.hitos_desarrollo ||
      consulta?.perimetro_cefalico ||
      consulta?.es_longitud ||
      consulta?.longitud ||
      consulta?.otros_pediatria;

    if (tienePediaConsulta) {
      engine.writeSubtitle("Pediatría");

      if (consulta?.hitos_desarrollo)
        engine.writeLabelValue(
          "Hitos del desarrollo",
          consulta.hitos_desarrollo
        );

      if (consulta?.perimetro_cefalico)
        engine.writeLabelValue(
          "Perímetro cefálico (cm)",
          consulta.perimetro_cefalico
        );

      if (consulta?.es_longitud)
        engine.writeLabelValue(
          "¿Usar longitud (no talla)?",
          consulta.es_longitud ? "Confirmado" : ""
        );

      if (consulta?.longitud)
        engine.writeLabelValue(
          "Longitud (cm)",
          consulta.longitud
        );

      if (consulta?.otros_pediatria)
        engine.writeLabelValue(
          "Otros (Pediatría)",
          consulta.otros_pediatria
        );
    }

    // ===============================
    // GERIATRÍA
    // ===============================

    const tieneGeriaConsulta =
      consulta?.caidas_ultimos_12_meses ||
      consulta?.detalle_caidas_12_meses ||
      consulta?.dependencia_aseo_personal ||
      consulta?.otros_geriatria;

    if (tieneGeriaConsulta) {
      engine.writeSubtitle("Geriatría");

      if (consulta?.caidas_ultimos_12_meses)
        engine.writeLabelValue(
          "Caídas últimos 12 meses",
          consulta.caidas_ultimos_12_meses ? "Confirmado" : ""
        );

      if (consulta?.detalle_caidas_12_meses)
        engine.writeLabelValue(
          "Detalle de caídas",
          consulta.detalle_caidas_12_meses
        );

      if (consulta?.dependencia_aseo_personal)
        engine.writeLabelValue(
          "Dependencia en aseo personal",
          consulta.dependencia_aseo_personal ? "Confirmado" : ""
        );

      if (consulta?.otros_geriatria)
        engine.writeLabelValue(
          "Otros (Geriatría)",
          consulta.otros_geriatria
        );
    }


    engine.addVerticalSpace(10);
    

    if (index < consultas.length - 1) {
   engine.startNewPage();
 }
  });
}

// ===============================
// ANEXO I — SIGNOS VITALES
// ===============================

export function imprimirAnexoSignos(engine, registros = []) {
  engine.writeTitle("ANEXO I — Evolución de Signos Vitales");

  // Columnas extra solo si algún registro tiene el dato (igual que en pantalla)
  const conCintura = registros.some((r) => tieneMedicion(r.cintura));
  const conGlucosa = registros.some((r) => tieneMedicion(r.glucosa));

  const columns = ["Fecha", "Peso", "Talla", "TA", "FC", "FR", "Temp", "SpO2", "IMC"];
  const colWidths = [26, 14, 14, 18, 12, 12, 13, 13, 14];

  if (conCintura) {
    columns.push("Cintura");
    colWidths.push(15);
  }
  if (conGlucosa) {
    columns.push("Glucosa");
    colWidths.push(15);
  }

  engine.drawSimpleTable({
    columns,
    // Si no hay columnas extra, se deja el acomodo de siempre
    colWidths: conCintura || conGlucosa ? colWidths : undefined,
    rows: registros.map((r) => {
      const fila = [r.fecha, r.peso, r.talla, r.ta, r.fc, r.fr, r.temp, r.spo2, r.imc];
      if (conCintura) fila.push(tieneMedicion(r.cintura) ? r.cintura : "");
      if (conGlucosa) fila.push(tieneMedicion(r.glucosa) ? r.glucosa : "");
      return fila;
    }),
  });
}


